// src/components/ExcelGrid.jsx

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

const DEFAULT_FONT_SIZE_ = "text-xs";
const MONO_FONT_SIZE = "text-xs";

// Utility to clamp a value between min/max
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Normalize for stable equality checks
const norm = (v) => (v == null ? "" : String(v).trim());

// Clipboard-safe text conversion
const toText = (v) => (v == null ? "" : String(v));

function normalizeSelectOption(opt) {
  if (opt == null) return null;
  if (typeof opt === "string") {
    return { value: opt, label: opt };
  }
  return {
    value: opt.value ?? "",
    label: opt.label ?? opt.value ?? "",
  };
}

function getSelectOptions(col, row) {
  const raw =
    typeof col.optionsFn === "function" ? col.optionsFn(row) : col.options || [];
  return raw.map(normalizeSelectOption).filter(Boolean);
}

function getSelectValueSet(col, row) {
  return new Set(getSelectOptions(col, row).map((opt) => String(opt.value)));
}

function getSelectDisplayLabel(col, row, value) {
  const rawValue = value == null ? "" : String(value);
  const match = getSelectOptions(col, row).find(
    (opt) => String(opt.value) === rawValue
  );
  return match ? match.label : rawValue;
}

// Explicit numeric-column detection, with backward-compatible fallbacks
const isNumericColumn = (col) =>
  !!col?.numeric ||
  !!col?.className?.includes?.("text-right") ||
  /amount|requirement|value|principal|coupon|yield|price|rate|par|cost|payment|deposit/i.test(
    col?.key || ""
  );

// Explicit date-column detection, with backward-compatible fallbacks
const isDateColumn = (col) =>
  !!col?.date ||
  col?.editor === "date" ||
  col?.key === "date" ||
  /(^|_)(date)$|_date$|Date$/.test(col?.key || "");

// Convert supported UI/raw date inputs to canonical ISO YYYY-MM-DD
function toIsoDate(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  // Already ISO or ISO-like datetime
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${y}-${m}-${d}`;
  }

  // MM/DD/YYYY or M/D/YYYY
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    const mm = String(Number(m)).padStart(2, "0");
    const dd = String(Number(d)).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  // MM-DD-YYYY or M-D-YYYY
  const dashUsMatch = raw.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashUsMatch) {
    const [, m, d, y] = dashUsMatch;
    const mm = String(Number(m)).padStart(2, "0");
    const dd = String(Number(d)).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  return raw;
}

// Light ISO date validity check
function isIsoDate(value) {
  if (!value) return false;
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// UI display formatter: ISO YYYY-MM-DD -> MM/DD/YYYY
function formatUiDate(value) {
  if (value == null) return "";
  const iso = toIsoDate(value);
  if (!isIsoDate(iso)) return String(value);

  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
}

// Default display formatter used when a column does not provide its own format/render
function getDisplayCellValue(col, row, rows, ri) {
  if (typeof col.render === "function") {
    return col.render(row, rows, ri);
  }

  const raw = row[col.key];

  if (col.editor === "select") {
    return getSelectDisplayLabel(col, row, raw);
  }

  if (typeof col.format === "function") {
    return col.format(raw, row, ri);
  }

  if (isDateColumn(col)) {
    return formatUiDate(raw);
  }

  return raw;
}

function getPlaceholder(col, row, rows, ri) {
  if (typeof col.placeholder === "function") {
    return col.placeholder(row, rows, ri) || "";
  }
  return col.placeholder || "";
}

function isMonoColumn(col) {
  return !!col?.mono;
}

function getCellFontClassName(
  col,
  { defaultCellFontClassName, monoCellFontClassName }
) {
  if (col?.fontClassName) return col.fontClassName;
  if (isMonoColumn(col)) return monoCellFontClassName;
  return defaultCellFontClassName;
}

function getCellFontSizeClassName(
  col,
  { defaultCellFontSizeClassName, monoCellFontSizeClassName }
) {
  if (col?.fontSizeClassName) return col.fontSizeClassName;
  if (isMonoColumn(col)) return monoCellFontSizeClassName;
  return defaultCellFontSizeClassName;
}

function getRowClassName(rowClassName, row, ri) {
  if (typeof rowClassName === "function") {
    return rowClassName(row, ri) || "";
  }
  return rowClassName || "";
}

function getCellClassName(col, row, rows, ri) {
  if (typeof col?.cellClassName === "function") {
    return col.cellClassName(row, rows, ri) || "";
  }
  if (typeof col?.className === "function") {
    return col.className(row, rows, ri) || "";
  }
  return col?.className || "";
}

function isColumnEditable(col, row) {
  if (typeof col?.editable === "function") {
    return !!col.editable(row);
  }
  return !!col?.editable;
}

// src/components/ExcelGrid.jsx

export default function ExcelGrid({
  columns = [],
  rows = [],
  onChange = () => {},
  requestEditCell = null,
  onSelectionChange = null,
  showCopyButton = true,
  copyTableColumns = null,
  copyTableRows = null,
  copyTableHeading = "",
  copyTableAsHtml = false,
  toolbarActions = null,
  rowClassName = null,
  viewportConstrained = false,
  viewportHeight = null,
  viewportMaxHeight = null,

  allowPasteAppendRows = false,
  makePasteRow = null,

  defaultCellFontClassName = "text-app",
  defaultCellFontSizeClassName = DEFAULT_FONT_SIZE_,
  monoCellFontClassName = "font-mono text-app",
  monoCellFontSizeClassName = MONO_FONT_SIZE,
}) {
  // States
  const [selected, setSelected] = useState({ row: 0, col: 0 });
  const [selectionRange, setSelectionRange] = useState(null); // {start, end}
  const [editing, setEditing] = useState(null); // {row, col}
  const [editBuffer, setEditBuffer] = useState(null); // For type-to-edit
  const [isDragging, setIsDragging] = useState(false);
  const [isFillDragging, setIsFillDragging] = useState(false);
  const [fillStart, setFillStart] = useState(null);
  const [fillEnd, setFillEnd] = useState(null);
  const [copyState, setCopyState] = useState("idle"); // idle | copied | error
  const [availableViewportHeight, setAvailableViewportHeight] = useState(null);

  const tableRef = useRef();
  const detachedHeaderRef = useRef();
  const clipboardInputRef = useRef();
  const copyResetTimerRef = useRef(null);

  const numRows = rows.length;
  const numCols = columns.length;
  const hasGroupedHeaders = columns.some((column) => column.headerGroup);
  const usesDetachedHeader = viewportConstrained && hasGroupedHeaders;

  // Firefox does not reliably dispatch paste events when a non-editable
  // element has focus. Keep an inert text input focused while a grid cell is
  // selected so clipboard and keyboard events bubble through the grid in all
  // supported browsers.
  const focusGrid = () => {
    clipboardInputRef.current?.focus({ preventScroll: true });
  };

  useLayoutEffect(() => {
    if (!viewportConstrained || !tableRef.current) {
      setAvailableViewportHeight(null);
      return undefined;
    }

    const updateMaxHeight = () => {
      const { top } = tableRef.current.getBoundingClientRect();
      const availableHeight = Math.max(240, window.innerHeight - top - 16);
      setAvailableViewportHeight(availableHeight);
    };

    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);

    return () => window.removeEventListener("resize", updateMaxHeight);
  }, [viewportConstrained]);

  const constrainedMaxHeight = viewportMaxHeight ?? availableViewportHeight;

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  // notify parent when selection changes
  useEffect(() => {
    if (typeof onSelectionChange !== "function") return;
    onSelectionChange({ selected, selectionRange });
  }, [selected, selectionRange, onSelectionChange]);

  // Respond to parent request to focus & edit a specific cell
  useEffect(() => {
    if (
      requestEditCell &&
      (editing?.row !== requestEditCell.row ||
        editing?.col !== requestEditCell.col)
    ) {
      setSelected({ row: requestEditCell.row, col: requestEditCell.col });
      setEditing({ row: requestEditCell.row, col: requestEditCell.col });
      setEditBuffer(null);
    }
  }, [requestEditCell, editing]);

  const isEventFromEditor = (e) => {
    const t = e.target;
    return (
      t.tagName === "INPUT" ||
      t.tagName === "SELECT" ||
      t.tagName === "BUTTON" ||
      t.closest?.("input,select,button")
    );
  };

  function isCellSelected(r, c) {
    if (!selectionRange)
      return selected && selected.row === r && selected.col === c;
    const [r1, r2] = [selectionRange.start.row, selectionRange.end.row].sort(
      (a, b) => a - b
    );
    const [c1, c2] = [selectionRange.start.col, selectionRange.end.col].sort(
      (a, b) => a - b
    );
    return r >= r1 && r <= r2 && c >= c1 && c <= c2;
  }

  function isCellInRange(r, c, range) {
    if (!range) return false;
    const [r1, r2] = [range.start.row, range.end.row].sort((a, b) => a - b);
    const [c1, c2] = [range.start.col, range.end.col].sort((a, b) => a - b);
    return r >= r1 && r <= r2 && c >= c1 && c <= c2;
  }

  function isCellInFill(r, c) {
    if (!fillStart || !fillEnd) return false;
    return isCellInRange(r, c, { start: fillStart, end: fillEnd });
  }

  function isSelectionEdge(ri, ci, edge) {
    let r1, r2, c1, c2;
    if (selectionRange) {
      [r1, r2] = [selectionRange.start.row, selectionRange.end.row].sort(
        (a, b) => a - b
      );
      [c1, c2] = [selectionRange.start.col, selectionRange.end.col].sort(
        (a, b) => a - b
      );
    } else if (selected) {
      r1 = r2 = selected.row;
      c1 = c2 = selected.col;
    }
    if (edge === "top") return ri === r1 && ci >= c1 && ci <= c2;
    if (edge === "bottom") return ri === r2 && ci >= c1 && ci <= c2;
    if (edge === "left") return ci === c1 && ri >= r1 && ri <= r2;
    if (edge === "right") return ci === c2 && ri >= r1 && ri <= r2;
    return false;
  }

  // Compute a clipboard value that matches what the grid displays
  function getCopyCellValue(ri, ci, sourceColumns = columns, sourceRows = rows) {
    const col = sourceColumns[ci];
    const row = sourceRows[ri];

    if (!col || !row) return "";

    if (typeof col.copyValue === "function") {
      return toText(col.copyValue(row, sourceRows, ri));
    }

    const display = getDisplayCellValue(col, row, sourceRows, ri);

    if (
      typeof display === "string" ||
      typeof display === "number" ||
      typeof display === "boolean"
    ) {
      return toText(display);
    }

    return toText(row[col.key]);
  }

  function buildClipboardText({
    range = null,
    includeHeaders = false,
    fallbackToWholeGrid = false,
    sourceColumns = columns,
    sourceRows = rows,
  } = {}) {
    let effectiveRange = range;

    if (!effectiveRange && fallbackToWholeGrid) {
      if (!sourceRows.length || !sourceColumns.length) return "";
      effectiveRange = {
        start: { row: 0, col: 0 },
        end: {
          row: sourceRows.length - 1,
          col: sourceColumns.length - 1,
        },
      };
    }

    if (!effectiveRange) return "";

    const [r1, r2] = [effectiveRange.start.row, effectiveRange.end.row].sort(
      (a, b) => a - b
    );
    const [c1, c2] = [effectiveRange.start.col, effectiveRange.end.col].sort(
      (a, b) => a - b
    );

    const block = [];

    if (includeHeaders) {
      if (sourceColumns.some((column) => column.headerGroup)) {
        const groupHeaderVals = [];
        for (let ci = c1; ci <= c2; ci++) {
          const group = sourceColumns[ci]?.headerGroup || "";
          const previousGroup = ci > c1 ? sourceColumns[ci - 1]?.headerGroup || "" : null;
          groupHeaderVals.push(toText(ci === c1 || group !== previousGroup ? group : ""));
        }
        block.push(groupHeaderVals.join("\t"));
      }
      const headerVals = [];
      for (let ci = c1; ci <= c2; ci++) {
        headerVals.push(
          toText(sourceColumns[ci]?.label ?? sourceColumns[ci]?.key ?? "")
        );
      }
      block.push(headerVals.join("\t"));
    }

    for (let ri = r1; ri <= r2; ri++) {
      const rowVals = [];
      for (let ci = c1; ci <= c2; ci++) {
        rowVals.push(getCopyCellValue(ri, ci, sourceColumns, sourceRows));
      }
      block.push(rowVals.join("\t"));
    }

    return block.join("\n");
  }

  function escapeClipboardHtml(value) {
    return toText(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function buildClipboardHtml({ sourceColumns, sourceRows, heading = "" }) {
    const hasHeaderGroups = sourceColumns.some((column) => column.headerGroup);
    const headerCells = sourceColumns
      .map(
        (col) =>
          `<td style="white-space:nowrap">${escapeClipboardHtml(
            col.label ?? col.key ?? ""
          )}</td>`
      )
      .join("");
    const groupHeaderCells = hasHeaderGroups
      ? sourceColumns.reduce((groups, col) => {
          const label = col.headerGroup || "";
          const previous = groups[groups.length - 1];
          if (label && previous?.label === label) previous.span += 1;
          else groups.push({ label, span: 1 });
          return groups;
        }, []).map((group) => group.label
          ? `<td colspan="${group.span}" style="white-space:nowrap;text-align:center">${escapeClipboardHtml(group.label)}</td>`
          : `<td colspan="${group.span}" style="white-space:nowrap"></td>`
        ).join("")
      : "";
    const headingRow = heading
      ? `<tr><td style="white-space:nowrap">${escapeClipboardHtml(
          heading
        )}</td></tr>`
      : "";
    const bodyRows = sourceRows
      .map((row, ri) => {
        const cells = sourceColumns
          .map((col, ci) => {
            const hasExcelValue = typeof col.excelValue === "function";
            const value = hasExcelValue
              ? col.excelValue(row, sourceRows, ri)
              : getCopyCellValue(ri, ci, sourceColumns, sourceRows);
            const numberFormat = col.excelNumberFormat
              ? `;mso-number-format:'${escapeClipboardHtml(
                  col.excelNumberFormat
                )}'`
              : "";
            return `<td style="white-space:nowrap${numberFormat}">${escapeClipboardHtml(
              value
            )}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    return `<table>${headingRow}${hasHeaderGroups ? `<tr>${groupHeaderCells}</tr>` : ""}<tr>${headerCells}</tr>${bodyRows}</table>`;
  }

  async function copyText(text, html = "") {
    if (!text) return;

    try {
      if (
        html &&
        typeof ClipboardItem !== "undefined" &&
        typeof navigator.clipboard?.write === "function"
      ) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": new Blob([text], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      setCopyState("copied");
    } catch {
      try {
        await navigator.clipboard.writeText(text);
        setCopyState("copied");
      } catch {
        setCopyState("error");
      }
    }

    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current);
    }

    copyResetTimerRef.current = setTimeout(() => {
      setCopyState("idle");
    }, 1500);
  }

  async function handleCopyButton() {
    const hasRange = !!selectionRange;
    const tableColumns = copyTableColumns || columns;
    const tableRows = copyTableRows || rows;
    let text = hasRange
      ? buildClipboardText({
          range: selectionRange,
          includeHeaders: false,
          fallbackToWholeGrid: false,
        })
      : buildClipboardText({
          range: null,
          includeHeaders: true,
          fallbackToWholeGrid: true,
          sourceColumns: tableColumns,
          sourceRows: tableRows,
        });

    if (!hasRange && copyTableHeading && text) {
      text = `${toText(copyTableHeading)}\n${text}`;
    }

    const html =
      !hasRange && copyTableAsHtml
        ? buildClipboardHtml({
            sourceColumns: tableColumns,
            sourceRows: tableRows,
            heading: copyTableHeading,
          })
        : "";

    await copyText(text, html);

    focusGrid();
  }

  function handleCellMouseDown(r, c, e) {
    if (isEventFromEditor(e)) return;

    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;

    setSelected({ row: r, col: c });

    const onMove = (ev) => {
      if (!moved) {
        const dx = Math.abs(ev.clientX - startX);
        const dy = Math.abs(ev.clientY - startY);
        if (dx > 2 || dy > 2) {
          moved = true;
          setIsDragging(true);
          setSelectionRange({
            start: { row: r, col: c },
            end: { row: r, col: c },
          });
          tableRef.current?.classList.add("dragging");
        }
      }
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);

      tableRef.current?.classList.remove("dragging");

      if (!moved) {
        setIsDragging(false);
        setSelectionRange(null);
        focusGrid();
      } else {
        setIsDragging(false);
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  function handleCellMouseOver(r, c) {
    if (isDragging) {
      setSelectionRange((range) =>
        range
          ? { start: range.start, end: { row: r, col: c } }
          : { start: { row: r, col: c }, end: { row: r, col: c } }
      );
    }
    if (isFillDragging) {
      setFillEnd({ row: r, col: c });
    }
  }

  function handleMouseUp() {
    const wasFillDragging = isFillDragging;

    tableRef.current?.classList.remove("dragging");

    setIsDragging(false);
    setIsFillDragging(false);

    if (wasFillDragging && fillStart && fillEnd) {
      const [r1, r2] = [fillStart.row, fillEnd.row].sort((a, b) => a - b);
      const [c1, c2] = [fillStart.col, fillEnd.col].sort((a, b) => a - b);
      const anchorValue = rows[fillStart.row][columns[fillStart.col].key];

      let changed = false;
      const newRows = rows.map((row, ri) => {
        if (ri < r1 || ri > r2) return row;
        let newRow = { ...row };
        for (let ci = c1; ci <= c2; ci++) {
          if (isColumnEditable(columns[ci], rows[ri])) {
            const key = columns[ci].key;
            if (norm(newRow[key]) !== norm(anchorValue)) {
              newRow[key] = anchorValue;
              changed = true;
            }
          }
        }
        return newRow;
      });

      if (changed) onChange(newRows);
      setFillStart(null);
      setFillEnd(null);
    }
  }

  function findDataEdge(row, col, dir) {
    if (dir === "up") {
      let r = row;
      while (r > 0 && rows[r - 1][columns[col].key] !== "") r--;
      return r;
    }
    if (dir === "down") {
      let r = row;
      while (r < numRows - 1 && rows[r + 1][columns[col].key] !== "") r++;
      return r;
    }
    if (dir === "left") {
      let c = col;
      while (c > 0 && rows[row][columns[c - 1].key] !== "") c--;
      return c;
    }
    if (dir === "right") {
      let c = col;
      while (c < numCols - 1 && rows[row][columns[c + 1].key] !== "") c++;
      return c;
    }
    return dir === "up" || dir === "down" ? row : col;
  }

  function handleKeyDown(e) {
    // Start editing by typing
    if (
      !editing &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      e.key.length === 1 &&
      isColumnEditable(columns[selected?.col], rows[selected?.row])
    ) {
      const col = columns[selected.col];
      if (col.editor === "select") {
        setEditing(selected);
        setEditBuffer(rows[selected.row]?.[col.key] ?? "");
        e.preventDefault();
        return;
      }
      if (col.editor === "date" || isDateColumn(col)) {
        setEditing(selected);
        setEditBuffer(null);
        e.preventDefault();
        return;
      }
      if (/^[0-9A-Za-z.\-/]$/.test(e.key)) {
        setEditing(selected);
        setEditBuffer(e.key);
        e.preventDefault();
        return;
      }
    }

    if (editing) {
      const { row, col } = editing;
      const activeCol = columns[col];

      if (activeCol.editor === "select") {
        if (e.key === "Escape") {
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }
      } else {
        const colKey = activeCol.key;
        let val = editBuffer != null ? editBuffer : rows[row][colKey];

        if (["ArrowRight", "Tab"].includes(e.key)) {
          handleEditCommit(row, col, val);
          let nrow = row,
            ncol = col + 1;
          if (ncol >= numCols) {
            ncol = 0;
            nrow = clamp(row + 1, 0, numRows - 1);
          }
          setSelected({ row: nrow, col: ncol });
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }

        if (e.key === "Enter") {
          handleEditCommit(row, col, val);
          let nrow = clamp(
            row + (e.shiftKey ? -1 : 1),
            0,
            numRows - 1
          );
          setSelected({ row: nrow, col });
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }

        if (e.key === "ArrowLeft") {
          handleEditCommit(row, col, val);
          let ncol = clamp(col - 1, 0, numCols - 1);
          setSelected({ row, col: ncol });
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }

        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          handleEditCommit(row, col, val);
          let nrow = clamp(
            row + (e.key === "ArrowUp" ? -1 : 1),
            0,
            numRows - 1
          );
          setSelected({ row: nrow, col });
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }

        if (e.key === "Escape") {
          setEditing(null);
          setEditBuffer(null);
          e.preventDefault();
          return;
        }
      }
    }

    let { row, col } = selected || { row: 0, col: 0 };

    if (e.key === "Escape" && !editing) {
      setSelected(null);
      setSelectionRange(null);
      e.preventDefault();
      return;
    }

    if (
      !editing &&
      e.shiftKey &&
      (e.ctrlKey || e.metaKey) &&
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      let start = selectionRange
        ? selectionRange.start
        : selected || { row: 0, col: 0 };
      let end = selectionRange ? selectionRange.end : start;

      const [c1, c2] = [start.col, end.col].sort((a, b) => a - b);
      const [r1, r2] = [start.row, end.row].sort((a, b) => a - b);

      if (e.key === "ArrowUp") {
        const newRow = Math.min(
          ...Array.from({ length: c2 - c1 + 1 }, (_, i) =>
            findDataEdge(r1, c1 + i, "up")
          )
        );
        start = { row: newRow, col: c1 };
        end = { row: r2, col: c2 };
      }
      if (e.key === "ArrowDown") {
        const newRow = Math.max(
          ...Array.from({ length: c2 - c1 + 1 }, (_, i) =>
            findDataEdge(r2, c1 + i, "down")
          )
        );
        start = { row: r1, col: c1 };
        end = { row: newRow, col: c2 };
      }
      if (e.key === "ArrowLeft") {
        const newCol = Math.min(
          ...Array.from({ length: r2 - r1 + 1 }, (_, i) =>
            findDataEdge(r1 + i, c1, "left")
          )
        );
        start = { row: r1, col: newCol };
        end = { row: r2, col: c2 };
      }
      if (e.key === "ArrowRight") {
        const newCol = Math.max(
          ...Array.from({ length: r2 - r1 + 1 }, (_, i) =>
            findDataEdge(r1 + i, c2, "right")
          )
        );
        start = { row: r1, col: c1 };
        end = { row: r2, col: newCol };
      }

      setSelectionRange({ start, end });
      setSelected(end);
      e.preventDefault();
      return;
    }

    if (
      !editing &&
      e.shiftKey &&
      !e.ctrlKey &&
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      let start = selectionRange
        ? selectionRange.start
        : selected || { row: 0, col: 0 };
      let end = selectionRange ? { ...selectionRange.end } : { ...start };

      if (e.key === "ArrowUp") end.row = clamp(end.row - 1, 0, numRows - 1);
      if (e.key === "ArrowDown") end.row = clamp(end.row + 1, 0, numRows - 1);
      if (e.key === "ArrowLeft") end.col = clamp(end.col - 1, 0, numCols - 1);
      if (e.key === "ArrowRight") end.col = clamp(end.col + 1, 0, numCols - 1);

      setSelectionRange({ start, end });
      setSelected(end);
      e.preventDefault();
      return;
    }

    if (
      !editing &&
      !e.shiftKey &&
      !e.ctrlKey &&
      ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      let nrow = row;
      let ncol = col;
      if (e.key === "ArrowUp") nrow = clamp(row - 1, 0, numRows - 1);
      if (e.key === "ArrowDown") nrow = clamp(row + 1, 0, numRows - 1);
      if (e.key === "ArrowLeft") ncol = clamp(col - 1, 0, numCols - 1);
      if (e.key === "ArrowRight") ncol = clamp(col + 1, 0, numCols - 1);

      setSelected({ row: nrow, col: ncol });
      setSelectionRange(null);
      e.preventDefault();
      return;
    }

    if (!editing && (e.key === "Tab" || e.key === "Enter")) {
      e.preventDefault();
      let nrow = row;
      let ncol = col;

      if (e.key === "Tab") {
        if (e.shiftKey) {
          ncol = col - 1;
          if (ncol < 0) {
            ncol = numCols - 1;
            nrow = clamp(row - 1, 0, numRows - 1);
          }
        } else {
          ncol = col + 1;
          if (ncol >= numCols) {
            ncol = 0;
            nrow = clamp(row + 1, 0, numRows - 1);
          }
        }
      } else if (e.key === "Enter") {
        nrow = clamp(row + (e.shiftKey ? -1 : 1), 0, numRows - 1);
      }

      setSelected({ row: nrow, col: ncol });
      setSelectionRange(null);
      return;
    }

    // Copy (Ctrl/Cmd+C) - copy displayed values for current selection/single cell
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
      e.preventDefault();

      const copyRange =
        selectionRange || (selected && { start: selected, end: selected });

      if (!copyRange) return;

      const text = buildClipboardText({
        range: copyRange,
        includeHeaders: false,
        fallbackToWholeGrid: false,
      });

      copyText(text);
      return;
    }
  }

  function handlePaste(e) {
    e.preventDefault();

    const raw = e.clipboardData.getData("text") ?? "";
    if (!raw) return;

    let r1, r2, c1, c2;
    if (selectionRange) {
      [r1, r2] = [selectionRange.start.row, selectionRange.end.row].sort(
        (a, b) => a - b
      );
      [c1, c2] = [selectionRange.start.col, selectionRange.end.col].sort(
        (a, b) => a - b
      );
    } else if (selected) {
      r1 = r2 = selected.row;
      c1 = c2 = selected.col;
    } else {
      r1 = r2 = c1 = c2 = 0;
    }

    const selRows = r2 - r1 + 1;
    const selCols = c2 - c1 + 1;

    const lines = raw.split(/\r?\n/).filter((ln) => ln.length > 0);
    const grid = lines.map((ln) => ln.split("\t"));
    const clipRows = grid.length;
    const clipCols = Math.max(...grid.map((r) => r.length));

    const newRows = rows.map((row) => ({ ...row }));
    let changed = false;

    const ensureRow = (ri) => {
      if (ri < newRows.length) return true;
      if (!allowPasteAppendRows) return false;

      while (newRows.length <= ri) {
        newRows.push(
          typeof makePasteRow === "function"
            ? makePasteRow({}, newRows.length)
            : { id: Date.now() + newRows.length }
        );
      }

      return true;
    };

    const coerceForCol = (ri, ci, val) => {
      const col = columns[ci];
      let v = val;

      if (col.editor === "select") {
        const valueSet = getSelectValueSet(col, newRows[ri] || rows[ri]);
        const candidate = String(val ?? "").trim();
        return candidate && valueSet.has(candidate) ? candidate : "";
      }

      if (isNumericColumn(col)) {
        const cleaned = String(val).replace(/[^\d.-]/g, "");
        v = cleaned === "" ? null : Number(cleaned);
        return v;
      }

      if (isDateColumn(col)) {
        const iso = toIsoDate(val);
        v = iso === "" ? "" : iso;
        return v;
      }

      return v;
    };

    if (clipRows === 1 && clipCols === 1 && (selRows > 1 || selCols > 1)) {
      const single = grid[0][0];

      for (let ri = r1; ri <= r2; ri++) {
        if (!ensureRow(ri)) continue;

        for (let ci = c1; ci <= c2; ci++) {
          if (ci < 0 || ci >= columns.length) continue;
          if (!isColumnEditable(columns[ci], newRows[ri])) continue;

          const val = coerceForCol(ri, ci, single);
          if (norm(newRows[ri][columns[ci].key]) !== norm(val)) {
            newRows[ri][columns[ci].key] = val;
            changed = true;
          }
        }
      }
    } else {
      for (let i = 0; i < clipRows; i++) {
        const ri = r1 + i;
        if (!ensureRow(ri)) continue;

        for (let j = 0; j < (grid[i]?.length ?? 0); j++) {
          const ci = c1 + j;
          if (ci < 0 || ci >= columns.length) continue;
          if (!isColumnEditable(columns[ci], newRows[ri])) continue;

          const val = coerceForCol(ri, ci, grid[i][j]);
          if (norm(newRows[ri][columns[ci].key]) !== norm(val)) {
            newRows[ri][columns[ci].key] = val;
            changed = true;
          }
        }
      }
    }

    if (changed) onChange(newRows);
  }

  function handleEditCommit(r, c, value) {
    const col = columns[c];
    const colKey = col.key;
    const oldVal = rows[r][colKey];

    let val = value;

    if (col.editor === "select") {
      const valueSet = getSelectValueSet(col, rows[r]);
      const candidate = String(value ?? "").trim();
      val = candidate && valueSet.has(candidate) ? candidate : "";
    } else if (isNumericColumn(col)) {
      const cleaned = String(value).replace(/[^\d.-]/g, "");
      val = cleaned === "" ? null : Number(cleaned);
    } else if (isDateColumn(col)) {
      const iso = toIsoDate(value);
      val = iso === "" ? "" : iso;
    }

    if (norm(oldVal) === norm(val)) {
      setEditing(null);
      setEditBuffer(null);
      focusGrid();
      return;
    }

    const newRows = rows.map((row, i) =>
      i === r ? { ...row, [colKey]: val } : row
    );

    setEditing(null);
    setEditBuffer(null);
    onChange(newRows);
    focusGrid();
  }

  function renderFillHandle(r, c) {
    if (!isCellSelected(r, c) || !isColumnEditable(columns[c], rows[r])) return null;
    return (
      <div
        className="fill-handle absolute right-0 bottom-0 w-2 h-2 rounded-sm bg-brand cursor-crosshair"
        style={{ zIndex: 2 }}
        onMouseDown={(e) => handleCellMouseDown(r, c, e)}
        title="Drag to fill"
      />
    );
  }

  const copyButtonLabel =
    copyState === "copied"
      ? "Copied"
      : copyState === "error"
      ? "Copy failed"
      : "Copy table";

  return (
    <div className="excel-grid-wrapper">
      {showCopyButton || toolbarActions ? (
        <div className="mb-2 flex items-center justify-start gap-3">
          {showCopyButton ? (
            <button
              type="button"
              className="linkCls text-xs"
              onClick={handleCopyButton}
              onMouseDown={(e) => e.stopPropagation()}
              title={
                selectionRange
                  ? "Copy selected cells"
                  : "Copy table with headers"
              }
            >
              {copyButtonLabel}
            </button>
          ) : null}
          {toolbarActions}
        </div>
      ) : null}

      <div className="relative">
        {usesDetachedHeader ? (
          <div ref={detachedHeaderRef} className="overflow-hidden bg-muted">
            <table className="w-full table-fixed border border-border select-none text-sm">
              <colgroup>{columns.map((col) => <col key={col.key} className={col.width || "min-w-[80px]"} />)}</colgroup>
              <thead>
                <tr className="bg-muted">
                  {columns.reduce((groups, column) => {
                    const previous = groups[groups.length - 1];
                    if (column.headerGroup && previous?.label === column.headerGroup) previous.span += 1;
                    else groups.push({ label: column.headerGroup || "", span: 1, className: column.headerGroupClassName || "" });
                    return groups;
                  }, []).map((group, index) => (
                    <th key={`${group.label || "blank"}-${index}`} colSpan={group.span} className={["h-11 border-b border-border bg-muted p-2 text-center text-muted-foreground", group.className || "whitespace-nowrap", !group.label && index === 0 ? "sticky left-0 z-10" : ""].filter(Boolean).join(" ")}>
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr className="bg-muted">
                  {columns.map((col, ci) => (
                    <th key={col.key} className={["border-b border-border bg-muted p-2 text-muted-foreground", ci === 0 ? "sticky left-0 z-10" : "", col.width || "min-w-[80px]", col.headerClassName || "text-left"].filter(Boolean).join(" ")}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        ) : null}
        <div
          className={[
            "excel-grid-container pb-2",
            viewportConstrained ? "isolate overflow-auto" : "overflow-x-auto",
          ].join(" ")}
          tabIndex={0}
          ref={tableRef}
          onKeyDown={handleKeyDown}
          onMouseUp={handleMouseUp}
          onPaste={handlePaste}
          onScroll={(event) => {
            if (detachedHeaderRef.current) detachedHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
          }}
          style={{
            outline: "none",
            ...(viewportHeight
              ? { height: typeof viewportHeight === "number" ? `${viewportHeight}px` : viewportHeight }
              : constrainedMaxHeight ? { maxHeight: `${constrainedMaxHeight}px` } : {}),
          }}
        >
        <input
          ref={clipboardInputRef}
          type="text"
          value=""
          onChange={() => {}}
          tabIndex={-1}
          aria-label="Grid clipboard target"
          className="fixed h-px w-px opacity-0 pointer-events-none"
          style={{ left: "-10000px", top: 0 }}
        />
        <table className={`w-full border border-border select-none text-sm ${usesDetachedHeader ? "table-fixed border-t-0" : ""}`}>
          {usesDetachedHeader ? <colgroup>{columns.map((col) => <col key={col.key} className={col.width || "min-w-[80px]"} />)}</colgroup> : null}
          {!usesDetachedHeader ? <thead>
            {hasGroupedHeaders ? (
              <tr className="bg-muted/60">
                {columns.reduce((groups, column) => {
                  const previous = groups[groups.length - 1];
                  if (column.headerGroup && previous?.label === column.headerGroup) previous.span += 1;
                  else groups.push({ label: column.headerGroup || "", span: 1, className: column.headerGroupClassName || "" });
                  return groups;
                }, []).map((group, index) => (
                  <th
                    key={`${group.label || "blank"}-${index}`}
                    colSpan={group.span}
                    className={[
                      "h-11 border-b border-border bg-muted p-2 text-center text-muted-foreground",
                      group.className || "whitespace-nowrap",
                      viewportConstrained ? "sticky top-0 z-40" : "",
                      viewportConstrained && !group.label && index === 0 ? "left-0 z-50" : "",
                    ].filter(Boolean).join(" ")}
                  >
                    {group.label}
                  </th>
                ))}
              </tr>
            ) : null}
            <tr className="bg-muted/60">
              {columns.map((col, ci) => (
                <th
                  key={col.key}
                  className={[
                    "p-2 text-muted-foreground border-b border-border",
                    "bg-muted",
                    viewportConstrained ? `sticky ${hasGroupedHeaders ? "top-11" : "top-0"} z-40` : "",
                    viewportConstrained && ci === 0
                      ? "left-0 z-50"
                      : "",
                    col.width || "min-w-[80px]",
                    col.headerClassName || "text-left",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead> : null}
          <tbody>
            {rows.map((row, ri) => (
              <tr
                key={row.id ?? ri}
                className={getRowClassName(rowClassName, row, ri)}
              >
                {columns.map((col, ci) => {
                  const isInFill = isCellInFill(ri, ci);
                  const isEditingCell =
                    editing && editing.row === ri && editing.col === ci;
                  const showPerimeter =
                    !!selectionRange ||
                    (selected && selected.row === ri && selected.col === ci);
                  const numericEditorClass = isNumericColumn(col)
                    ? "numeric text-right"
                    : "";

                  return (
                    <td
                      key={col.key}
                      className={[
                        "p-2 border border-border align-middle",
                        isCellSelected(ri, ci) && isColumnEditable(col, row)
                          ? "relative"
                          : "",
                        viewportConstrained && ci === 0
                          ? "sticky left-0 z-10"
                          : "",
                        col.width || "min-w-[80px]",
                        getCellFontClassName(col, {
                          defaultCellFontClassName,
                          monoCellFontClassName,
                        }),
                        getCellFontSizeClassName(col, {
                          defaultCellFontSizeClassName,
                          monoCellFontSizeClassName,
                        }),
                        getCellClassName(col, row, rows, ri),
                        isColumnEditable(col, row)
                          ? "bg-blue-50 dark:bg-blue-900/30 cursor-pointer"
                          : "bg-app",
                        selectionRange && isCellSelected(ri, ci)
                          ? "bg-gray-200"
                          : "",
                        showPerimeter && isSelectionEdge(ri, ci, "top")
                          ? "border-t-2 border-t-brand"
                          : "",
                        showPerimeter && isSelectionEdge(ri, ci, "bottom")
                          ? "border-b-2 border-b-brand"
                          : "",
                        showPerimeter && isSelectionEdge(ri, ci, "left")
                          ? "border-l-2 border-l-brand"
                          : "",
                        showPerimeter && isSelectionEdge(ri, ci, "right")
                          ? "border-r-2 border-r-brand"
                          : "",
                        isInFill ? "bg-brand/20" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      tabIndex={-1}
                      onClick={(e) => {
                        if (isEventFromEditor(e)) return;
                        if (!isColumnEditable(col, row)) return;

                        // Keep date cells in selection mode on a single click.
                        // Firefox's native date control swallows clipboard events
                        // when one of its internal date segments has focus, which
                        // prevents the grid from handling multi-row paste. Date
                        // editing remains available via double-click or typing.
                        if (col.editor === "select") {
                          setEditing({ row: ri, col: ci });
                          setEditBuffer(row[col.key] ?? "");
                        }
                      }}
                      onDoubleClick={(e) => {
                        if (isEventFromEditor(e)) return;
                        if (!isColumnEditable(col, row)) return;
                        setEditing({ row: ri, col: ci });
                      }}
                      onMouseDown={(e) => handleCellMouseDown(ri, ci, e)}
                      onMouseOver={() => handleCellMouseOver(ri, ci)}
                    >
                      {isEditingCell ? (
                        col.editor === "select" ? (
                          <select
                            autoFocus
                            className={[
                              "w-full px-1 py-0.5 text-app bg-app outline-none border-none shadow-none",
                              numericEditorClass,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            value={
                              editBuffer !== null ? editBuffer : row[col.key] ?? ""
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onMouseUp={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              const val = e.target.value;
                              setEditBuffer(val);
                              handleEditCommit(ri, ci, val);
                              setEditing(null);
                              setEditBuffer(null);
                              focusGrid();
                            }}
                            onBlur={() => {
                              setEditing(null);
                              setEditBuffer(null);
                              focusGrid();
                            }}
                          >
                            <option value="">Select type...</option>
                            {getSelectOptions(col, row).map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : isDateColumn(col) ? (
                          <input
                            type="date"
                            autoFocus
                            className="w-full px-1 py-0.5 text-app bg-app outline-none border-none shadow-none"
                            value={
                              editBuffer !== null
                                ? toIsoDate(editBuffer)
                                : row[col.key]
                                ? toIsoDate(row[col.key])
                                : ""
                            }
                            onChange={(e) => setEditBuffer(e.target.value)}
                            onBlur={(e) => {
                              handleEditCommit(ri, ci, e.target.value);
                              setEditBuffer(null);
                              setEditing(null);
                              focusGrid();
                            }}
                          />
                        ) : (
                          <input
                            autoFocus
                            className={[
                              "w-full px-1 py-0.5 text-app bg-app outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 border-none shadow-none",
                              numericEditorClass,
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            value={
                              editBuffer !== null ? editBuffer : row[col.key] ?? ""
                            }
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onMouseUp={(e) => e.stopPropagation()}
                            onChange={(e) => setEditBuffer(e.target.value)}
                            onBlur={(e) => {
                              handleEditCommit(ri, ci, e.target.value);
                              setEditBuffer(null);
                              setEditing(null);
                              focusGrid();
                            }}
                          />
                        )
                      ) : (
                        <div className="whitespace-pre">
                          {(() => {
                            const display = getDisplayCellValue(col, row, rows, ri);
                            const isBlank =
                              display == null || String(display).trim() === "";

                            if (isBlank && isColumnEditable(col, row)) {
                              const hint = getPlaceholder(col, row, rows, ri);
                              return hint ? (
                                <span className="text-muted">{hint}</span>
                              ) : (
                                ""
                              );
                            }

                            return display;
                          })()}
                        </div>
                      )}
                      {renderFillHandle(ri, ci)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        {viewportConstrained && !usesDetachedHeader ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-1 inset-x-0 z-50 h-1 border-b border-border bg-app"
          />
        ) : null}
      </div>
    </div>
  );
}
